import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  SkipNext as NextIcon,
  SkipPrevious as PreviousIcon
} from '@mui/icons-material';

import HistoricalTrackMap from '../common/HistoricalTrackMap';
import { getDriverTrackHistory, getGpsAnomalyEvents } from '../../services/driverTrackService';

const pad = value => String(value).padStart(2, '0');
const toLocalInput = date => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
);
const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const driverLabel = driver => {
  const number = driver?.driverNumber || driver?.user?.driverNumber;
  const name = driver?.name || driver?.displayName || driver?.user?.name || driver?.email || driver?.id;
  return String(number ? `${number} - ${name || ''}` : (name || ''));
};

const vehicleLabel = vehicle => {
  const number = vehicle?.vehicleNumber;
  const plate = vehicle?.licensePlate || vehicle?.plate || vehicle?.id;
  return String(number ? `${number} - ${plate || ''}` : (plate || ''));
};

const PHASE_LABELS = {
  idle: 'Frei',
  to_pickup: 'Anfahrt',
  at_pickup: 'Am Abholort',
  passenger_on_board: 'Fahrgast an Bord',
  paused: 'Pause'
};

// Zusaetzliche „Phase" fuer die Auswertung der GPS-Manipulationserkennung.
// Bewusst hier statt auf einer eigenen Seite: Fahrer und Zeitraum sind schon
// gewaehlt, und die Karte zeigt die gefahrene Strecke — der gemeldete Sprung
// laesst sich direkt daneben nachvollziehen.
// nextcab_mobile/docs/general/gps_manipulation_erkennung_konzept.md
const GPS_PHASE = 'gps_anomalies';

const GPS_TYPE_LABELS = {
  mock_provider: 'Standort-Simulation (Fake-GPS-App)',
  teleport: 'Unmöglicher Standortsprung',
  time_anomaly: 'Zeitstempel aus der Zukunft'
};

const GPS_ACTION_LABELS = {
  blocked: 'Fahrer gesperrt',
  queue_penalty: 'Sektor-Position entzogen',
  logged: 'nur protokolliert'
};

const GPS_FLAG_LABELS = {
  speedZeroWhileMoving: 'Bewegung ohne Geschwindigkeit',
  constantAccuracy: 'konstante Genauigkeit',
  noHeading: 'keine Fahrtrichtung'
};

const formatCoords = point => (
  point && Number.isFinite(point.lat) && Number.isFinite(point.lng)
    ? `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`
    : '-'
);

const formatTime = value => value ? new Date(value).toLocaleString('de-DE') : '-';

const DriverHistoryTab = ({ drivers = [], vehicles = [] }) => {
  const [startValue, setStartValue] = useState(() => toLocalInput(startOfToday()));
  const [endValue, setEndValue] = useState(() => toLocalInput(new Date()));
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [phase, setPhase] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [gpsEvents, setGpsEvents] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  const sortedDrivers = useMemo(
    () => [...drivers].filter(driver => driver?.id).sort((a, b) => driverLabel(a).localeCompare(driverLabel(b), 'de')),
    [drivers]
  );
  const sortedVehicles = useMemo(
    () => [...vehicles].filter(vehicle => vehicle?.id).sort((a, b) => vehicleLabel(a).localeCompare(vehicleLabel(b), 'de')),
    [vehicles]
  );
  const points = result?.points || [];
  const summary = result?.summary || {};
  const selectedPoint = points[selectedIndex] || null;

  useEffect(() => {
    if (!playing || points.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setSelectedIndex(current => {
        if (current >= points.length - 1) {
          setPlaying(false);
          return current;
        }
        return current + 1;
      });
    }, 650);
    return () => window.clearInterval(timer);
  }, [playing, points.length]);

  const applyPreset = preset => {
    const start = new Date();
    const end = new Date();
    if (preset === 'yesterday') {
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else {
      start.setHours(0, 0, 0, 0);
    }
    setStartValue(toLocalInput(start));
    setEndValue(toLocalInput(end));
  };

  const loadHistory = async () => {
    const start = new Date(startValue);
    const end = new Date(endValue);
    if (!selectedDriver && !selectedVehicle) {
      setError('Bitte einen Fahrer oder ein Fahrzeug auswählen.');
      return;
    }
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start >= end) {
      setError('Bitte einen gültigen Zeitraum auswählen.');
      return;
    }
    if (end.getTime() - start.getTime() > 24 * 60 * 60 * 1000) {
      setError('Der Detailzeitraum darf höchstens 24 Stunden umfassen.');
      return;
    }
    setLoading(true);
    setError('');
    setPlaying(false);
    const isGpsView = phase === GPS_PHASE;
    try {
      // In der GPS-Ansicht wird die Strecke ungefiltert geladen — sie ist der
      // Kontext, in dem ein gemeldeter Sprung erst beurteilbar wird.
      const data = await getDriverTrackHistory({
        startMs: start.getTime(),
        endMs: end.getTime(),
        driverId: selectedDriver?.id || '',
        vehicleId: selectedVehicle?.id || '',
        phase: isGpsView ? 'all' : phase
      });
      setResult(data);
      setSelectedIndex(0);

      if (isGpsView) {
        const events = await getGpsAnomalyEvents({
          startMs: start.getTime(),
          endMs: end.getTime(),
          driverId: selectedDriver?.id || '',
          limit: 200
        });
        setGpsEvents(events?.events || []);
      } else {
        setGpsEvents(null);
      }
    } catch (loadError) {
      setResult(null);
      setGpsEvents(null);
      setError(loadError?.message || 'Das Fahrer-Tracking konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ lg: 'center' }} sx={{ mb: 2 }}>
        <TextField
          label="Von"
          type="datetime-local"
          size="small"
          value={startValue}
          onChange={event => setStartValue(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Bis"
          type="datetime-local"
          size="small"
          value={endValue}
          onChange={event => setEndValue(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Stack direction="row" spacing={0.5}>
          <Button size="small" variant="outlined" onClick={() => applyPreset('today')}>Heute</Button>
          <Button size="small" variant="outlined" onClick={() => applyPreset('yesterday')}>Gestern</Button>
        </Stack>
        <Autocomplete
          size="small"
          sx={{ minWidth: 230, flex: 1 }}
          options={sortedDrivers}
          value={selectedDriver}
          getOptionLabel={driverLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => { setSelectedDriver(value); if (value) setSelectedVehicle(null); }}
          renderInput={params => <TextField {...params} label="Fahrer" placeholder="Nummer oder Name" />}
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 230, flex: 1 }}
          options={sortedVehicles}
          value={selectedVehicle}
          getOptionLabel={vehicleLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => { setSelectedVehicle(value); if (value) setSelectedDriver(null); }}
          renderInput={params => <TextField {...params} label="Fahrzeug" placeholder="Nummer oder Kennzeichen" />}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Phase</InputLabel>
          <Select label="Phase" value={phase} onChange={event => setPhase(event.target.value)}>
            <MenuItem value="all">Alle Phasen</MenuItem>
            {Object.entries(PHASE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
            <MenuItem value={GPS_PHASE}>GPS-Auffälligkeiten</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<SearchIcon />} onClick={loadHistory} disabled={loading}>
          Anzeigen
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>}

      {!loading && result && (
        <>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip label={`${summary.pointCount || 0} Punkte`} />
            <Chip label={`${Number(summary.distanceKm || 0).toLocaleString('de-DE')} km`} />
            <Chip label={`Ø Genauigkeit ${summary.averageAccuracyMeters ?? '-'} m`} />
            <Chip
              color={(summary.largestGapSeconds || 0) > 180 ? 'warning' : 'default'}
              label={`Größte Lücke ${Math.round((summary.largestGapSeconds || 0) / 60)} min`}
            />
          </Stack>

          {/* GPS-Auffaelligkeiten: an `gpsEvents` gebunden, nicht an der
              Dropdown-Auswahl — sonst wechselt die Tabelle schon beim
              Umstellen, bevor neu geladen wurde. */}
          {gpsEvents !== null && (
            gpsEvents.length === 0 ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Im gewählten Zeitraum wurden keine GPS-Auffälligkeiten protokolliert.
              </Alert>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  {gpsEvents.length} Auffälligkeit(en). Ein einzelner Eintrag ist noch kein Beweis —
                  vergleichen Sie ihn mit der Strecke unten. Wirkt ein Wert falsch, melden Sie ihn uns:
                  die Erkennungsschwellen (1,5&nbsp;km, 200&nbsp;km/h) sind fest eingestellt und werden
                  anhand solcher Rückmeldungen nachjustiert.
                </Alert>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell>Zeitpunkt</TableCell>
                      <TableCell>Art</TableCell>
                      <TableCell>Von</TableCell>
                      <TableCell>Nach</TableCell>
                      <TableCell align="right">Distanz</TableCell>
                      <TableCell align="right">Zeitabstand</TableCell>
                      <TableCell align="right">Errechnet</TableCell>
                      <TableCell align="right">Genauigkeit</TableCell>
                      <TableCell>Zielsektor</TableCell>
                      <TableCell>Erkannt durch</TableCell>
                      <TableCell>Reaktion</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{gpsEvents.map(event => {
                      const flags = Object.entries(event.flags || {})
                        .filter(([, value]) => value === true)
                        .map(([key]) => GPS_FLAG_LABELS[key] || key);
                      const accuracy = [event.from?.acc, event.to?.acc]
                        .filter(value => Number.isFinite(value))
                        .map(value => `${Math.round(value)} m`)
                        .join(' / ');
                      return (
                        <TableRow key={event.id} hover>
                          <TableCell>{formatTime(event.createdAtMs)}</TableCell>
                          <TableCell>
                            {GPS_TYPE_LABELS[event.type] || event.type}
                            {flags.length > 0 && (
                              <Typography variant="caption" color="text.secondary" component="div">
                                {flags.join(', ')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{formatCoords(event.from)}</TableCell>
                          <TableCell>{formatCoords(event.to)}</TableCell>
                          <TableCell align="right">
                            {Number.isFinite(event.distanceMeters)
                              ? `${(event.distanceMeters / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} km`
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {Number.isFinite(event.dtSeconds) ? `${Math.round(event.dtSeconds)} s` : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {Number.isFinite(event.impliedKmh)
                              ? `${Math.round(event.impliedKmh).toLocaleString('de-DE')} km/h`
                              : '-'}
                          </TableCell>
                          <TableCell align="right">{accuracy || '-'}</TableCell>
                          <TableCell>{event.sectorName || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={event.detectedBy === 'server' ? 'Serverprüfung' : 'App-Erkennung'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={event.action === 'blocked' ? 'error' : 'default'}
                              label={GPS_ACTION_LABELS[event.action] || event.action}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}</TableBody>
                  </Table>
                </TableContainer>
              </>
            )
          )}

          {points.length > 0 ? (
            <>
              <HistoricalTrackMap
                points={points}
                selectedIndex={selectedIndex}
                onSelectedIndexChange={setSelectedIndex}
                height={480}
              />
              <Paper variant="outlined" sx={{ mt: 1.5, px: 2, py: 1.5 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title="Vorheriger Punkt"><span><IconButton size="small" disabled={selectedIndex === 0} onClick={() => setSelectedIndex(i => Math.max(0, i - 1))}><PreviousIcon /></IconButton></span></Tooltip>
                    <Tooltip title={playing ? 'Pause' : 'Wiedergabe'}><IconButton size="small" onClick={() => setPlaying(value => !value)}>{playing ? <PauseIcon /> : <PlayIcon />}</IconButton></Tooltip>
                    <Tooltip title="Nächster Punkt"><span><IconButton size="small" disabled={selectedIndex >= points.length - 1} onClick={() => setSelectedIndex(i => Math.min(points.length - 1, i + 1))}><NextIcon /></IconButton></span></Tooltip>
                  </Stack>
                  <Slider
                    min={0}
                    max={Math.max(0, points.length - 1)}
                    value={Math.min(selectedIndex, Math.max(0, points.length - 1))}
                    onChange={(_, value) => setSelectedIndex(Number(value))}
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                  <Box sx={{ minWidth: 250 }}>
                    <Typography variant="body2" fontWeight={600}>{formatTime(selectedPoint?.t)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {PHASE_LABELS[selectedPoint?.phase] || 'Unbekannt'} · Genauigkeit {Math.round(selectedPoint?.acc || 0)} m
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </>
          ) : (
            <Alert severity="info">Für diesen Zeitraum wurden keine Standortpunkte gefunden.</Alert>
          )}

          {gpsEvents === null && result.segments?.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  <TableCell>Start</TableCell><TableCell>Ende</TableCell><TableCell>Phase</TableCell>
                  <TableCell>Fahrer</TableCell><TableCell>Fahrzeug</TableCell><TableCell>Fahrt</TableCell><TableCell align="right">Punkte</TableCell>
                </TableRow></TableHead>
                <TableBody>{result.segments.map(segment => (
                  <TableRow key={segment.id} hover>
                    <TableCell>{formatTime(segment.startedAtMs)}</TableCell>
                    <TableCell>{formatTime(segment.endedAtMs)}</TableCell>
                    <TableCell>{PHASE_LABELS[segment.phase] || segment.phase}</TableCell>
                    <TableCell>{segment.driverNumber ? `${segment.driverNumber} - ` : ''}{segment.driverName || '-'}</TableCell>
                    <TableCell>{segment.vehicleNumber ? `${segment.vehicleNumber} - ` : ''}{segment.licensePlate || '-'}</TableCell>
                    <TableCell>{segment.rideId ? segment.rideId.slice(0, 10) : '-'}</TableCell>
                    <TableCell align="right">{segment.pointCount}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default DriverHistoryTab;
